<template>
    <div class="ui-page">
        <ui-page-header title="Incluir Lista" back="/lista-credores" back-text="Lista de Credores">
            <v-tooltip bottom>
                <template #activator="{ on }">
                    <v-btn v-if="$allowed('edit')" v-on="on" color="primary" depressed @click="submitImport" :loading="loading">
                        Enviar lista
                    </v-btn>
                </template>
                <span>Enviar arquivo para processamento</span>
            </v-tooltip>
        </ui-page-header>

        <ui-section title="Detalhes do documento">
            <template #actions>
                <v-tooltip bottom>
                    <template #activator="{ on }">
                        <v-icon v-on="on" color="primary" class="cursor-pointer" @click="dialogFormat = true">
                            mdi-help-circle-outline
                        </v-icon>
                    </template>
                    <span>Ver formatação e baixar template</span>
                </v-tooltip>
            </template>

            <v-form :readonly="!$allowed('save')" ref="form">
                <v-row>
                    <v-col cols="12" md="6">
                        <v-text-field v-model="form.name" label="Nome" :rules="[$rules.required]" outlined dense hide-details="auto" />
                    </v-col>

                    <v-col cols="12" md="6">
                        <v-autocomplete v-model="form.client_id" :items="clients" item-text="label" item-value="value"
                            label="Caso (obrigatório)" :rules="[$rules.required]" outlined dense hide-details="auto" 
                            no-data-text="Nenhum caso encontrado" @change="onClientChange" />
                    </v-col>
                </v-row>

                <v-row class="mt-3">
                    <v-col cols="12" md="6">
                        <v-text-field v-model="form.email" label="E-mail (recebimento de notificação)" 
                            type="email" :rules="[$rules.required, $rules.email]" outlined dense hide-details="auto" />
                    </v-col>

                    <v-col cols="12" md="6">
                        <label class="creditor-file-label">Lista de Credores <span class="error--text">*</span></label>
                        <div
                            class="creditor-file-dropzone"
                            :class="{
                                'creditor-file-dropzone--active': isDraggingFile,
                                'creditor-file-dropzone--selected': !!form.file,
                                'creditor-file-dropzone--error': !!fileValidationError,
                            }"
                            role="button"
                            tabindex="0"
                            @click="openFilePicker"
                            @keydown.enter.prevent="openFilePicker"
                            @keydown.space.prevent="openFilePicker"
                            @dragenter.prevent="isDraggingFile = true"
                            @dragover.prevent="isDraggingFile = true"
                            @dragleave.prevent.self="isDraggingFile = false"
                            @drop.prevent="onFileDrop"
                        >
                            <input
                                ref="creditorListFilePicker"
                                class="creditor-file-picker"
                                type="file"
                                accept=".csv,.xlsx,.xls"
                                @change="onFilePickerChange"
                            />

                            <v-icon size="34" :color="form.file ? 'primary' : 'grey'">
                                {{ form.file ? 'mdi-file-check-outline' : 'mdi-cloud-upload-outline' }}
                            </v-icon>

                            <div class="creditor-file-dropzone__content">
                                <template v-if="form.file">
                                    <strong>{{ form.file.name }}</strong>
                                    <span>{{ formatFileSize(form.file.size) }}</span>
                                </template>
                                <template v-else>
                                    <strong>Arraste o arquivo para esta área</strong>
                                    <span>ou clique para selecionar</span>
                                </template>
                                <small>Formatos aceitos: CSV, XLS e XLSX · máximo de 130 MB</small>
                            </div>

                            <v-btn v-if="(form.file) && $allowed('delete')" icon small aria-label="Remover arquivo selecionado"
                                @click.stop="clearSelectedFile">
                                <v-icon>mdi-close</v-icon>
                            </v-btn>
                        </div>
                        <div v-if="fileValidationError" class="error--text caption mt-1">
                            {{ fileValidationError }}
                        </div>
                    </v-col>

                    <v-col cols="12" md="6" v-if="clientCompanyOptions.length">
                        <v-select v-model="form.client_company_id" :items="clientCompanyOptions"
                            item-text="name" item-value="id" label="Empresa do grupo" outlined dense
                            hide-details="auto" clearable no-data-text="Nenhuma empresa encontrada" />
                    </v-col>

                    <!-- Versao do QGC que recebe a lista (Edital 52 por padrao ou um QGC cadastrado) -->
                    <v-col cols="12" md="6" v-if="form.client_id">
                        <v-select v-model="form.version" :items="versionOptions" item-text="label" item-value="id"
                            label="QGC de destino" outlined dense hide-details="auto"
                            hint="Os créditos da planilha entram nesta versão do QGC" persistent-hint />
                    </v-col>

                    <v-col cols="12" class="mt-2">
                        <v-btn v-if="$allowed('edit')" outlined color="primary" small :loading="previewValidating" @click="validatePreview">
                            Validar arquivo antes de enviar
                        </v-btn>
                        <span v-if="previewResult && !previewValidating" class="ml-3 caption"
                            :class="validationErrors.length ? 'error--text' : 'success--text'">
                            {{ previewSummary }}
                        </span>
                    </v-col>
                </v-row>

                <v-row v-if="progress !== null" class="mt-4">
                    <v-col cols="12">
                        <v-progress-linear v-model="progress" color="green" height="25" reactive rounded>
                            <strong class="white--text">{{ progress }} %</strong>
                        </v-progress-linear>
                    </v-col>
                </v-row>
            </v-form>
        </ui-section>

        <ui-section v-if="validationErrors.length && !(importErrors && importErrors.length)">
            <template #header>
                <h3 class="ui-section-title error--text">
                    <v-icon color="error" small class="mr-1">mdi-alert-circle</v-icon>
                    Erros encontrados na planilha ({{ validationErrors.length }})
                </h3>
            </template>
            <p class="caption mb-2">Corrija a planilha e selecione o arquivo novamente.<span v-if="validationErrors.length > 50"> Exibindo os 50 primeiros.</span></p>
            <v-simple-table dense>
                <thead>
                    <tr>
                        <th class="text-left">Linha</th>
                        <th class="text-left">Mensagem de Erro</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(e, idx) in validationErrors.slice(0, 50)" :key="idx">
                        <td class="font-weight-bold">{{ e.row || '-' }}</td>
                        <td>{{ e.message }}</td>
                    </tr>
                </tbody>
            </v-simple-table>
        </ui-section>

        <ui-section v-if="importErrors && importErrors.length">
            <template #header>
                <h3 class="ui-section-title error--text">
                    <v-icon color="error" small class="mr-1">mdi-alert-circle</v-icon>
                    Erros na importação (Verifique as linhas abaixo)
                </h3>
            </template>
            <v-simple-table dense>
                <thead>
                    <tr>
                        <th class="text-left">Linha</th>
                        <th class="text-left">Mensagem de Erro</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(e, idx) in importErrors.slice(0, 50)" :key="idx">
                        <td class="font-weight-bold">{{ e.line_number || e.row || '-' }}</td>
                        <td>{{ e.error || e.message || JSON.stringify(e.details) }}</td>
                    </tr>
                </tbody>
            </v-simple-table>
        </ui-section>

        <v-dialog v-model="dialogFormat" max-width="900px">
            <v-card class="ui-card">
                <div class="ui-card__header">
                    <h3 class="ui-section-title">Formato do Arquivo</h3>
                </div>

                <div class="ui-card__body">
                    <p>
                        Aceitamos arquivos nos formatos <strong>CSV</strong> ou <strong>XLSX</strong>.
                        Para CSV, são suportados delimitadores <strong>vírgula</strong> ou <strong>ponto-e-vírgula</strong>.
                        Ao selecionar o arquivo, cada linha é validada na própria página; o servidor confere de novo no envio.
                    </p>

                    <p class="mt-4">As colunas esperadas (mapeadas para o sistema) são:</p>

                    <ul>
                        <li><strong>Nome / Nome completo:</strong> nome do credor. Ex.: João Silva</li>
                        <li>
                            <strong>CPF/CNPJ / Registro:</strong> CPF ou CNPJ do credor (com ou sem formatação).<br />
                            <em>Obs: CPF tem 11 dígitos; CNPJ tem 14 dígitos. Máscaras serão removidas automaticamente.</em>
                        </li>
                        <li>
                            <strong>CPF/CNPJ ficto:</strong> marque <em>Sim</em> (ou S, X, 1) quando o documento do credor for desconhecido (opcional).<br />
                            <em>A linha dispensa a validação do documento. Se a célula de CPF/CNPJ vier vazia, o sistema gera um número
                            fictício iniciado em 000; se vier preenchida, o número informado é mantido. O credor fica sinalizado com um ícone
                            de alerta no QGC e na Central de Créditos até a regularização.</em>
                        </li>
                        <li>
                            <strong>Classe:</strong> identificador numérico da classe (ex.: 1,2,3) ou texto legível
                            (Classe I - Trabalhista, Classe II - Garantia Real, ...). A coluna no arquivo pode se chamar
                            <em>tipo</em> ou <em>classe</em>.
                        </li>
                        <li><strong>Valor:</strong> valor do crédito (número, pode usar vírgula ou ponto decimal).</li>
                        <li><strong>Moeda:</strong> código/identificador de moeda (opcional, padrão = 1)</li>
                        <li><strong>E-mail:</strong> e-mail do credor (opcional)</li>
                        <li><strong>Telefone / Celular:</strong> telefone do credor com ou sem máscara (opcional)</li>
                        <li><strong>Empresa devedora / Empresa do grupo:</strong> empresa do grupo vinculada ao crédito (opcional)</li>
                    </ul>

                    <v-row class="mt-4">
                        <v-col cols="12" md="6">
                            <v-btn color="primary" @click="downloadTemplate" small elevation="0">
                                <v-icon left small>mdi-microsoft-excel</v-icon>
                                Baixar template (.xlsx)
                            </v-btn>
                        </v-col>
                    </v-row>

                    <p class="mt-6 font-weight-bold primary--text">Exemplo de registros de um arquivo válido:</p>

                    <v-simple-table dense class="elevation-1">
                        <thead class="grey lighten-4">
                            <tr>
                                <th>Nome</th>
                                <th>CPF/CNPJ</th>
                                <th>CPF/CNPJ ficto</th>
                                <th>Classe</th>
                                <th>Valor</th>
                                <th>Empresa devedora</th>
                                <th>E-mail</th>
                                <th>Telefone</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>João Silva</td>
                                <td>880.732.328-12</td>
                                <td>Não</td>
                                <td>Trabalhista</td>
                                <td>6292,29</td>
                                <td>Empresa do Grupo A</td>
                                <td>joaosilva@example.com</td>
                                <td>(11) 99999-9999</td>
                            </tr>
                            <tr>
                                <td>Bianca Siqueira</td>
                                <td>30436901412</td>
                                <td>Não</td>
                                <td>Garantia Real</td>
                                <td>5982,00</td>
                                <td>Empresa do Grupo B</td>
                                <td>bianca@example.com</td>
                                <td>(21) 98888-8888</td>
                            </tr>
                            <tr>
                                <td>Pedro Alencar</td>
                                <td>69.972.491/0001-39</td>
                                <td>Não</td>
                                <td>Quirografários</td>
                                <td>5142,00</td>
                                <td>Empresa do Grupo A</td>
                                <td>pedroalencar@example.com</td>
                                <td>(31) 97777-7777</td>
                            </tr>
                            <tr>
                                <td>Maria Sem Documento</td>
                                <td></td>
                                <td>Sim</td>
                                <td>Quirografários</td>
                                <td>3200,00</td>
                                <td>Empresa do Grupo A</td>
                                <td></td>
                                <td></td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </div>

                <div class="ui-card__footer">
                    <v-btn outlined @click="dialogFormat = false">Fechar</v-btn>
                </div>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { downloadXlsx } from "@/common/util/export-xlsx.util";
import axios from 'axios';
import creditorClassUtil from "@/common/util/creditor-class.util";
import { readCreditorListRows, validateCreditorListRows } from "@/common/util/creditor-list-validation.util";

export default {
    name: "CreditorsDetails",

    data: () => ({
        dialogFormat: false,
        loading: false,
        progress: null,

        form: {
            name: "",
            client_id: null,
            client_company_id: null,
            version: 1,
            email: "",
            file: null,
        },
        caseVersions: [],

        clients: [],
        clientObjects: [],
        importErrors: [],
        validationErrors: [],
        // Linhas lidas do arquivo, revalidadas ao trocar o caso (as classes sao do caso).
        previewRows: null,
        previewResult: null,
        previewValidating: false,
        isDraggingFile: false,
        fileValidationError: "",
    }),

    created() {
        this.loadClients();
    },

    computed: {
        previewSummary() {
            if (!this.previewResult) return '';
            const lines = `${this.previewResult.total} linha(s) lida(s)`;
            if (!this.validationErrors.length) return `${lines}, nenhum erro encontrado.`;
            return `${lines}, ${this.validationErrors.length} erro(s) encontrado(s).`;
        },

        versionOptions() {
            const base = [{ id: 1, label: 'v1 · Edital art. 52, § 1º' }];
            if (!this.caseVersions.length) return base;
            return this.caseVersions.map((item) => ({ id: item.version, label: `v${item.version} · ${item.title}${item.is_current ? ' (vigente)' : ''}` }));
        },

        selectedClient() {
            return this.clientObjects.find(client => Number(client.id) === Number(this.form.client_id)) || null;
        },

        clientCompanyOptions() {
            const companies = this.selectedClient && Array.isArray(this.selectedClient.companies)
                ? this.selectedClient.companies
                : [];

            return companies
                .filter(company => company && company.name)
                .map(company => ({ id: company.id, name: company.name }));
        },
    },

    methods: {
        // Veio do QGC: volta para o QGC do caso. Sem isso, para a lista antiga.
        goBack() {
            if (this.$route.query.from === "qgc" && this.$route.query.client_id) {
                this.$router.push({ path: "/qgc", query: { client_id: this.form.client_id || this.$route.query.client_id } });
                return;
            }
            this.$router.push("/lista-credores");
        },

        async loadClients() {
            try {
                const res = await axios.get('/api/clients', { params: { start: 1, limit: 100000, search: "{}" }});
                
                const data = res.data && res.data.data ? res.data.data.data : res.data.content.data;
                
                if (data) {
                    data.sort((a, b) => a.name.localeCompare(b.name));
                    this.clientObjects = data;
                    this.clients = data.map(c => ({ label: c.name || c.id, value: c.id }));
                    // Caso e versao vindos do QGC ja chegam preenchidos.
                    if (this.$route.query.client_id && !this.form.client_id) {
                        this.form.client_id = Number(this.$route.query.client_id);
                        this.onClientChange();
                        if (this.$route.query.version) this.form.version = Number(this.$route.query.version);
                    }
                }
            } catch (err) {
                console.error('Erro ao carregar clients', err);
                this.clients = [];
            }
        },

        onClientChange() {
            this.form.client_company_id = null;
            this.form.version = 1;
            this.loadCaseVersions();
            if (this.previewRows) this.runPreviewValidation();
        },

        async loadCaseVersions() {
            if (!this.form.client_id) { this.caseVersions = []; return; }
            try {
                const result = await axios.get(`/api/admin/clients/${this.form.client_id}/qgc-versions`);
                this.caseVersions = (result.data.content && result.data.content.versions) || [];
            } catch (error) {
                this.caseVersions = [];
            }
        },

        openFilePicker() {
            if (this.$refs.creditorListFilePicker) this.$refs.creditorListFilePicker.click();
        },

        onFilePickerChange(event) {
            const file = event.target.files && event.target.files[0];
            this.setSelectedFile(file);
            event.target.value = '';
        },

        onFileDrop(event) {
            this.isDraggingFile = false;
            const files = event.dataTransfer && event.dataTransfer.files;
            const file = files && files[0];

            if (files && files.length > 1) {
                this.$dialog.notify.info('Apenas o primeiro arquivo foi selecionado.', {
                    position: 'top-right',
                    timeout: 3000,
                });
            }

            this.setSelectedFile(file);
        },

        setSelectedFile(file) {
            if (!file) return;

            const extension = (file.name.split('.').pop() || '').toLowerCase();
            const allowedExtensions = ['csv', 'xls', 'xlsx'];

            if (!allowedExtensions.includes(extension)) {
                this.rejectSelectedFile('Formato inválido. Selecione um arquivo CSV, XLS ou XLSX.');
                return;
            }

            if (file.size > 130 * 1024 * 1024) {
                this.rejectSelectedFile('Arquivo deve ser inferior a 130 MB.');
                return;
            }

            this.fileValidationError = '';
            this.form.file = file;
            this.onFileChange(file);
        },

        rejectSelectedFile(message) {
            this.form.file = null;
            this.onFileChange(null);
            this.fileValidationError = message;
            this.$dialog.notify.error(message, { position: 'top-right', timeout: 4000 });
        },

        clearSelectedFile() {
            this.form.file = null;
            this.fileValidationError = '';
            this.onFileChange(null);
        },

        formatFileSize(size) {
            if (!Number(size)) return '0 KB';
            const megabytes = size / (1024 * 1024);
            return megabytes >= 1 ? `${megabytes.toFixed(2)} MB` : `${(size / 1024).toFixed(1)} KB`;
        },

        async downloadTemplate() {
            const rows = [
                ['Nome completo', 'CPF/CNPJ', 'CPF/CNPJ ficto', 'classe', 'valor', 'moeda', 'empresa devedora', 'email', 'telefone'],
                ['Empresa A', '880.732.328-12', 'Não', 'Classe I - Trabalhista', 10000, 1, 'Empresa do Grupo A', 'empresaA@exemplo.com', '(11) 99999-9999'],
                ['Empresa B', '69.972.491/0001-39', 'Não', 'Classe II - Garantia Real', 25000.5, 1, 'Empresa do Grupo B', 'empresaB@exemplo.com', '(21) 98888-8888'],
                ['Credor sem documento', '', 'Sim', 'Classe III - Quirografários', 5000, 1, 'Empresa do Grupo A', '', ''],
            ];
            await downloadXlsx(rows, 'modelo_lista_credores', { sheetName: 'Credores' });
        },


        async onFileChange(file) {
            this.validationErrors = [];
            this.importErrors = [];
            this.progress = null;
            this.previewRows = null;
            this.previewResult = null;

            if (!file) return;
            const f = Array.isArray(file) ? file[0] : file;
            if (!f) return;

            this.previewValidating = true;
            try {
                const rows = await readCreditorListRows(f);
                // Outro arquivo pode ter sido escolhido enquanto este era lido.
                if (this.form.file !== file) return;
                this.previewRows = rows;
                await this.runPreviewValidation();
            } catch (error) {
                if (this.form.file !== file) return;
                this.validationErrors = [{ row: 0, message: 'Erro ao processar o arquivo. Verifique se o formato está corrompido.' }];
                this.previewResult = { total: 0 };
            } finally {
                if (this.form.file === file) this.previewValidating = false;
            }
        },

        async runPreviewValidation() {
            if (!this.previewRows) return;
            const rows = this.previewRows;
            const classes = await creditorClassUtil.listByClient(this.form.client_id, { includeInactive: true });
            if (this.previewRows !== rows) return;
            const result = validateCreditorListRows(rows, classes);
            this.validationErrors = result.errors;
            this.previewResult = result;
        },

        async validatePreview() {
            if (!this.form.file) {
                this.$dialog.notify.error('Selecione um arquivo primeiro', { position: 'top-right', timeout: 3000 });
                return;
            }
            if (!this.form.client_id) {
                this.$dialog.notify.info('Selecione o caso para validar as classes cadastradas nele.', { position: 'top-right', timeout: 4000 });
            }
            if (!this.previewRows) await this.onFileChange(this.form.file);
            else await this.runPreviewValidation();

            if (this.validationErrors.length) {
                this.$dialog.notify.error(`${this.validationErrors.length} erro(s) na planilha. Veja a lista abaixo.`, { position: 'top-right', timeout: 5000 });
                return;
            }
            this.$dialog.notify.success(this.previewSummary, { position: 'top-right', timeout: 5000 });
        },

        async submitImport() {
            if (!this.$refs.form.validate()) return;

            if (!this.form.file || this.fileValidationError) {
                this.fileValidationError = this.fileValidationError || 'Selecione a lista de credores.';
                this.$dialog.notify.error(this.fileValidationError, { position: 'top-right', timeout: 3000 });
                return;
            }

            if (this.previewValidating) {
                this.$dialog.notify.info('Aguarde a validação da planilha terminar.', { position: 'top-right', timeout: 3000 });
                return;
            }

            if (this.validationErrors.length) {
                this.$dialog.notify.error(`Corrija os ${this.validationErrors.length} erro(s) da planilha antes de enviar.`, { position: 'top-right', timeout: 5000 });
                return;
            }

            const proceedAction = await this.$dialog.confirm({
                text: 'Deseja enviar a lista de credores?',
                title: 'Lista de credores',
                actions: { false: 'Cancelar', true: 'Enviar' }
            });

            if (!proceedAction) return;

            this.loading = true;
            this.progress = 0;
            this.importErrors = [];

            try {
                const fd = new FormData();
                const f = Array.isArray(this.form.file) ? this.form.file[0] : this.form.file;
                
                fd.append('name', this.form.name);
                fd.append('email', this.form.email);
                fd.append('client_id', this.form.client_id);
                if (this.form.client_company_id) fd.append('client_company_id', this.form.client_company_id);
                fd.append('version', this.form.version || 1);
                fd.append('file', f);

                const res = await axios.post('/api/admin/creditor-lists/upload', fd, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                    onUploadProgress: (event) => {
                        this.progress = Math.round((100 * event.loaded) / event.total);
                    }
                });

                const responseData = res.data.data || res.data.content;
                
                // Tratamento para caso a API retorne os erros de linha direto na resposta
                if (responseData && responseData.errors && responseData.errors.length) {
                    this.importErrors = responseData.errors;
                    this.progress = null;
                    this.$dialog.notify.error("Encontramos alguns erros na lista enviada, verifique-os abaixo!", { position: "top-right", timeout: 4000 });
                    return;
                }

                this.$dialog.notify.info("Lista de credores enviada com sucesso, aguarde o processamento", { position: "top-right", timeout: 3000 });
                this.goBack();

            } catch (err) {
                this.progress = null;
                const msg = (err.response && err.response.data && err.response.data.error && err.response.data.error.message) || err.message || 'Erro ao enviar arquivo!';
                this.$dialog.notify.error(msg, { position: 'top-right', timeout: 5000 });
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style scoped>

.cursor-pointer {
    cursor: pointer;
}

.creditor-file-label {
    display: block;
    margin: 0 0 6px 2px;
    color: var(--v-gray-darken1, #6b7280);
    font-size: 12px;
}

.creditor-file-dropzone {
    min-height: 112px;
    padding: 16px;
    border: 2px dashed var(--v-gray-base);
    border-radius: 8px;
    display: flex;
    align-items: center;
    gap: 14px;
    background: var(--v-white-base);
    cursor: pointer;
    transition: border-color 0.2s ease, background-color 0.2s ease, transform 0.2s ease;
    outline: none;
}

.creditor-file-dropzone:hover,
.creditor-file-dropzone:focus,
.creditor-file-dropzone--active {
    border-color: var(--v-primary-base);
    background: rgba(126, 85, 171, 0.06);
}

.creditor-file-dropzone--active {
    transform: scale(1.01);
}

.creditor-file-dropzone--selected {
    border-style: solid;
    border-color: var(--v-primary-base);
}

.creditor-file-dropzone--error {
    border-color: var(--v-error-base);
}

.creditor-file-dropzone__content {
    min-width: 0;
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.creditor-file-dropzone__content strong,
.creditor-file-dropzone__content span,
.creditor-file-dropzone__content small {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.creditor-file-dropzone__content span,
.creditor-file-dropzone__content small {
    color: var(--v-gray-darken1, #6b7280);
}

.creditor-file-picker {
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
    pointer-events: none;
}
</style>
